import type { sessionStats } from './useSessionStats';
import { useEffect, useRef } from 'react';
import { motionDuration } from './motionDuration';

export function SessionStrip({
  stats,
  reduced,
}: {
  stats: ReturnType<typeof sessionStats>;
  reduced: boolean;
}) {
  const streak = useRef<HTMLSpanElement>(null);
  const previous = useRef(stats.streak);
  useEffect(() => {
    const increased = stats.streak > previous.current;
    previous.current = stats.streak;
    if (!increased || reduced || !streak.current) return;
    const duration = motionDuration(streak.current);
    const animation = streak.current.animate(
      [
        { transform: 'translateY(6px)', opacity: 0.4 },
        { transform: 'translateY(0)', opacity: 1 },
      ],
      { duration, easing: 'ease-out' },
    );
    return () => animation.cancel();
  }, [stats.streak, reduced]);
  return (
    <dl className="session-strip" aria-label="This session" data-testid="session-stats">
      <div title="Tactics solved since this page loaded, excluding revealed solutions.">
        <dt>Solved</dt>
        <dd data-testid="session-solved">{stats.solved}</dd>
      </div>
      <div title="Consecutive first-try solves without mistakes or hints.">
        <dt>Streak</dt>
        <dd data-testid="session-streak">
          <span className="streak-value" ref={streak}>
            {stats.streak}
          </span>
        </dd>
      </div>
      <div title="Percentage of finished tactics solved without mistakes or hints; revealed solutions count as finished, but not first-try.">
        <dt>First try</dt>
        <dd data-testid="session-first-try">
          {stats.firstTry === null ? '—' : `${stats.firstTry}%`}
        </dd>
      </div>
    </dl>
  );
}
